import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";
import {
  Heart,
  Award,
  Sparkles,
  BookOpen,
  Users,
  Compass,
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  DollarSign,
  Calendar,
  MapPin,
  Star,
  Building,
  Mail,
  Phone,
  ChevronLeft,
  LogIn,
  LogOut,
  Check,
  HelpCircle,
  Download,
  FileSpreadsheet,
  Grid,
  Info,
  Clock,
  ArrowDown,
  Gift,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  GraduationCap,
  Briefcase,
  Target,
  MessageCircle,
  Ticket,
  Rocket
} from "lucide-react";
import { SEO } from "@/seo/SEO";
import { trackPageView } from "@/lib/analytics";
import { AUTOBOOKS_URL } from "@/lib/config";
import { DonateButton } from "@/components/DonateButton";
import { SupportAGG } from "@/components/SupportAGG";
import GivePage from "@/pages/GivePage";
import ShopPage from "@/pages/ShopPage";
import WaysToGivePage from "@/pages/WaysToGivePage";
import SchoolsPage from "@/pages/SchoolsPage";
import AdminDashboard from "@/admin/AdminDashboard";

// Setup Backend API URL and Client
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://thrive-lead.preview.emergentagent.com";
const API = `${BACKEND_URL}/api`;

// Ensure credentials are sent with all requests (essential for HTTPOnly Cookies)
axios.defaults.withCredentials = true;

// Helper to format API error message from FastAPI
function formatApiError(error) {
  if (!error) return "An unexpected error occurred.";
  const detail = error.response?.data?.detail;
  if (detail == null) return error.message || "An unexpected error occurred.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  }
  return String(detail);
}

// Logo Component
const BrandLogo = ({ variant = "default", imgClass = "h-[72px] w-[72px]", textClass = "text-2xl", gapClass = "gap-4", showText = true }) => {
  const heartLogoUrl = "https://customer-assets.emergentagent.com/job_0e489a82-2f44-4016-bd13-c06ced2d5c32/artifacts/wjrgajx6_AGG%20HEART%20LOGO.png";
  const whiteLogoUrl = "https://customer-assets.emergentagent.com/job_0e489a82-2f44-4016-bd13-c06ced2d5c32/artifacts/i193mubd_AGG%20WHITE%20LOGO.png";
  
  return (
    <div className={`flex items-center ${gapClass}`}>
      <img 
        src={variant === "white" ? whiteLogoUrl : heartLogoUrl} 
        alt="A Girl's Girl (AGG) logo" 
        className={`${imgClass} object-contain rounded-lg hover:scale-105 transition-all duration-300`}
      />
      {showText && (
        <span className={`font-serif ${textClass} tracking-wide font-bold whitespace-nowrap leading-tight ${variant === "white" ? "text-white" : "text-[#4B1E5A]"}`}>
          {"A GIRL'S GIRL"}
        </span>
      )}
    </div>
  );
};

// Glassmorphism Navbar
const Navbar = ({ adminUser, handleLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Programs", path: "/programs" },
    { name: "For Schools", path: "/schools" },
    { name: "Events", path: "/events" },
    { name: "Ways to Give", path: "/ways-to-give" },
    { name: "Shop", path: "/shop" },
    { name: "Contact", path: "/contact" }
  ];

  return (
    <nav className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${scrolled ? "bg-white/90 shadow-lg shadow-[#4B1E5A]/5" : "bg-white/80 border-b border-[#FFF5F9]"}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-28" : "h-32"}`}>
          {/* Logo on Left */}
          <Link to="/" data-testid="nav-logo" className="flex-shrink-0">
            <BrandLogo textClass="text-2xl" imgClass={scrolled ? "h-[96px] w-[96px]" : "h-[120px] w-[120px]"} />
          </Link>

          {/* Links Centered (Desktop) */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6 ml-4">
            {links.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`font-sans text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                    isActive 
                      ? "text-[#E94F9A] font-semibold border-b-2 border-[#E94F9A] pb-1" 
                      : "text-[#4A4A4A] hover:text-[#E94F9A]"
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* CTAs on Right */}
          <div className="hidden lg:flex items-center gap-4">
            {adminUser ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/admin"
                  data-testid="nav-admin-dashboard"
                  className="bg-[#4B1E5A] text-white rounded-full px-5 py-2 text-xs font-semibold hover:bg-[#E94F9A] transition-all"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="nav-btn-logout"
                  className="border border-[#4B1E5A] text-[#4B1E5A] rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#FFF5F9] transition-all"
                >
                  Log Out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/admin"
                  data-testid="nav-admin-login-link"
                  className="text-xs font-medium text-[#4A4A4A] hover:text-[#E94F9A] transition-colors mr-2 whitespace-nowrap"
                >
                  Admin Login
                </Link>
                <DonateButton variant="primary" testId="nav-btn-donate" />
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="nav-mobile-menu-toggle"
              className="text-[#4B1E5A] hover:text-[#E94F9A] p-2 focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {isOpen && (
        <div className="lg:hidden bg-white border-b border-[#FFF5F9] px-6 py-6 space-y-4">
          <div className="flex flex-col gap-4">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-mobile-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setIsOpen(false)}
                className="font-sans text-base font-medium text-[#4A4A4A] hover:text-[#E94F9A] block"
              >
                {link.name}
              </Link>
            ))}
            <hr className="border-[#FFF5F9]" />
            {adminUser ? (
              <div className="flex flex-col gap-3 pt-2">
                <Link
                  to="/admin"
                  data-testid="nav-mobile-admin"
                  onClick={() => setIsOpen(false)}
                  className="bg-[#4B1E5A] text-white text-center rounded-full py-3 font-semibold hover:bg-[#E94F9A] transition-all"
                >
                  Admin Dashboard
                </Link>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    handleLogout();
                  }}
                  data-testid="nav-mobile-logout"
                  className="border border-[#4B1E5A] text-[#4B1E5A] rounded-full py-3 font-semibold hover:bg-[#FFF5F9]"
                >
                  Log Out
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3 pt-2">
                <Link
                  to="/admin"
                  data-testid="nav-mobile-login"
                  onClick={() => setIsOpen(false)}
                  className="text-center text-sm font-medium text-[#4A4A4A] hover:text-[#E94F9A]"
                >
                  Admin Login
                </Link>
                <DonateButton variant="soft" className="w-full py-3" showTagline={false} testId="nav-mobile-donate" />
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [newsletterEnabled, setNewsletterEnabled] = useState(true);
  const [contact, setContact] = useState({ email: "info@shesagirlsgirl.org", phone: "980.406.5940", address: "Charlotte, North Carolina" });

  useEffect(() => {
    axios.get(`${API}/content-settings`).then((r) => {
      const d = r.data || {};
      if (d.newsletter_enabled === false) setNewsletterEnabled(false);
      setContact((c) => ({
        email: d.contact_email || c.email,
        phone: d.contact_phone || c.phone,
        address: d.contact_address || c.address,
      }));
    }).catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(response.data.message || "Thank you for subscribing!");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#4B1E5A] text-white pt-24 pb-10 px-6 sm:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
        
        {/* Brand Column */}
        <div className="md:col-span-4 space-y-6">
          <BrandLogo variant="white" imgClass="h-48 w-48" showText={false} />
          <p className="text-white/80 font-sans text-sm leading-relaxed max-w-sm">
            {"A Girl's Girl develops confident, emotionally healthy, future-ready young women through mentorship, leadership, and life readiness programming."}
          </p>
          <div className="flex items-center gap-3 pt-1">
            <a href="https://www.instagram.com/agirlsgirl_agg/" target="_blank" rel="noopener noreferrer" aria-label="A Girl's Girl on Instagram" data-testid="social-instagram" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#E94F9A] flex items-center justify-center transition-all hover:-translate-y-0.5">
              <Instagram className="h-4 w-4 text-white" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=61577959176157" target="_blank" rel="noopener noreferrer" aria-label="A Girl's Girl on Facebook" data-testid="social-facebook" className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#E94F9A] flex items-center justify-center transition-all hover:-translate-y-0.5">
              <Facebook className="h-4 w-4 text-white" />
            </a>
          </div>
        </div>

        {/* Quick Links Column */}
        <div className="md:col-span-3 grid grid-cols-2 gap-6">
          <div>
            <h4 className="font-serif text-sm font-semibold tracking-wider text-[#C8A96A] uppercase mb-4">{"Explore"}</h4>
            <div className="flex flex-col gap-2.5 text-sm text-white/80 font-sans">
              <Link to="/about" data-testid="footer-link-about" className="hover:text-[#E94F9A] transition-colors">{"About Us"}</Link>
              <Link to="/founder" data-testid="footer-link-founder" className="hover:text-[#E94F9A] transition-colors">{"Meet Founder"}</Link>
              <Link to="/programs" data-testid="footer-link-programs" className="hover:text-[#E94F9A] transition-colors">{"Programs"}</Link>
              <Link to="/who-we-serve" data-testid="footer-link-who-we-serve" className="hover:text-[#E94F9A] transition-colors">{"Who We Serve"}</Link>
              <Link to="/impact" data-testid="footer-link-impact" className="hover:text-[#E94F9A] transition-colors">{"Our Impact"}</Link>
            </div>
          </div>
          <div>
            <h4 className="font-serif text-sm font-semibold tracking-wider text-[#C8A96A] uppercase mb-4">{"Engage"}</h4>
            <div className="flex flex-col gap-2.5 text-sm text-white/80 font-sans">
              <Link to="/mentor" data-testid="footer-link-mentor" className="hover:text-[#E94F9A] transition-colors">{"Become Mentor"}</Link>
              <Link to="/volunteer" data-testid="footer-link-volunteer" className="hover:text-[#E94F9A] transition-colors">{"Volunteer"}</Link>
              <Link to="/schools" data-testid="footer-link-schools" className="hover:text-[#E94F9A] transition-colors">{"For Schools"}</Link>
              <Link to="/ways-to-give" data-testid="footer-link-ways" className="hover:text-[#E94F9A] transition-colors">{"Ways to Give"}</Link>
              <Link to="/shop" data-testid="footer-link-shop" className="hover:text-[#E94F9A] transition-colors">{"Shop AGG"}</Link>
              <a href={AUTOBOOKS_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-link-donate" className="hover:text-[#E94F9A] transition-colors">{"Donate Now"}</a>
              <Link to="/contact" data-testid="footer-link-contact" className="hover:text-[#E94F9A] transition-colors">{"Contact"}</Link>
            </div>
          </div>
        </div>

        {/* Newsletter + Contact Column */}
        <div className="md:col-span-5 space-y-6">
          {newsletterEnabled && (
            <div className="space-y-6" data-testid="footer-newsletter">
              <h4 className="font-serif text-sm font-semibold tracking-wider text-[#C8A96A] uppercase">{"Stay Connected"}</h4>
              <p className="text-white/80 font-sans text-sm">
                {"Subscribe for inspiration, impact updates, and event invitations."}
              </p>
              <form onSubmit={handleSubscribe} className="flex gap-2 max-w-sm">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  aria-label="Email address for newsletter"
                  data-testid="input-newsletter-email"
                  className="bg-white/10 text-white placeholder-white/40 border border-white/20 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E94F9A] flex-grow"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="btn-newsletter-subscribe"
                  className="bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-xl px-5 py-2.5 text-sm font-semibold transition-all"
                >
                  {loading ? "..." : "Subscribe"}
                </button>
              </form>
            </div>
          )}
          <div className="space-y-2.5 pt-2 text-sm text-white/80 font-sans">
            <a href={`mailto:${contact.email}`} data-testid="footer-email" className="flex items-center gap-3 hover:text-[#E94F9A] transition-colors">
              <Mail className="h-4 w-4 text-[#C8A96A]" />
              {contact.email}
            </a>
            <a href={`tel:${contact.phone.replace(/[^0-9+]/g, "")}`} data-testid="footer-phone" className="flex items-center gap-3 hover:text-[#E94F9A] transition-colors">
              <Phone className="h-4 w-4 text-[#C8A96A]" />
              {contact.phone}
            </a>
            <p className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-[#C8A96A]" />
              {contact.address}
            </p>
          </div>
        </div>

      </div>

      <div className="max-w-7xl mx-auto border-t border-white/10 mt-16 pt-8 space-y-4">
        <p className="text-xs text-white/50 font-sans text-center sm:text-left">
          {"A Girl's Girl is a nonprofit organization dedicated to empowering girls. 501(c)(3) status and EIN information will be displayed here once available."}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-white/50 font-sans gap-4">
          <p>{"© 2026 A Girl's Girl. All Rights Reserved."}</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[#E94F9A] transition-colors">{"Privacy Policy"}</a>
            <a href="#" className="hover:text-[#E94F9A] transition-colors">{"Terms of Service"}</a>
            <Link to="/admin" className="hover:text-[#E94F9A] transition-colors">{"Admin"}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Scrolling Carousel of Who We Serve / Partners
const PartnerCarousel = () => {
  const items = [
    { name: "Public Schools", desc: "Confidence Circles" },
    { name: "Foster Care Programs", desc: "Life Skills & Support" },
    { name: "Youth Group Homes", desc: "Safe Transformational Spaces" },
    { name: "Local Churches", desc: "Mentorship & Community" },
    { name: "Community Organizations", desc: "Inclusive Workshops" },
    { name: "Youth Leadership Programs", desc: "Public Speaking" }
  ];

  return (
    <div className="bg-[#FFF5F9]/40 py-10 border-y border-[#FFF5F9]">
      <div className="max-w-7xl mx-auto px-6 mb-4 flex justify-between items-center">
        <span className="font-serif text-xs font-semibold text-[#C8A96A] uppercase tracking-widest">{"WHO WE PARTNER WITH"}</span>
        <span className="text-xs font-sans text-[#4A4A4A]">{"Empowering through diverse community networks"}</span>
      </div>
      <div className="relative w-full overflow-hidden">
        <div className="flex animate-marquee gap-8 md:gap-12 py-3 w-max">
          {[...items, ...items].map((item, index) => (
            <div 
              key={index} 
              className="flex items-center gap-4 bg-white px-8 py-4 rounded-2xl border border-[#E5E5E5]/50 shadow-sm hover:border-[#E94F9A]/30 transition-all duration-300"
            >
              <div className="h-2 w-2 rounded-full bg-[#E94F9A]" />
              <div className="text-left">
                <p className="font-serif text-sm font-semibold text-[#4B1E5A]">{item.name}</p>
                <p className="font-sans text-[11px] text-[#4A4A4A] tracking-tight">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Home Page Components ---

const HomePage = () => {
  const navigate = useNavigate();
  const heroImage = "https://customer-assets.emergentagent.com/job_0e489a82-2f44-4016-bd13-c06ced2d5c32/artifacts/mb5jp0g8_AGG%20GIRLS%20PICTURE%20WITH%20BRANDED%20SHIRT.png";
  const founderImg = "https://customer-assets.emergentagent.com/job_thrive-lead/artifacts/fme160ev_Cherelle%20Portrait%20%282%29.jpg";
  const [hero, setHero] = useState({});
  useEffect(() => {
    axios.get(`${API}/content-settings`).then((r) => setHero(r.data || {})).catch(() => {});
  }, []);

  return (
    <div className="space-y-0" data-testid="home-page-container">
      
      {/* SECTION 1: HERO */}
      <section className="relative bg-white min-h-[90vh] flex items-center pt-12 pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          
          <div className="lg:col-span-6 space-y-8 text-left">
            <div className="inline-flex items-center gap-2 bg-[#FFF5F9] px-4 py-1.5 rounded-full fade-up delay-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#E94F9A]" />
              <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#E94F9A]">{hero.hero_eyebrow || "Where Confidence Meets Life Readiness"}</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#4B1E5A] leading-[1.1] font-bold fade-up delay-1 agg-heading-load">
              {hero.hero_title || "Where Confidence Meets Life Readiness"}
            </h1>
            <p className="font-serif text-xl sm:text-2xl text-[#C8A96A] italic leading-relaxed fade-up delay-2">
              {hero.hero_tagline || "The Confidence to Thrive. The Skills to Lead."}
            </p>
            <p className="font-sans text-base sm:text-lg text-[#4A4A4A] leading-relaxed max-w-xl fade-up delay-3">
              {hero.hero_paragraph || "Every girl deserves to know her worth, discover her voice, and believe her future is filled with possibility. At A Girl's Girl, we help girls build the confidence, leadership, and life readiness to thrive in school, in life, and beyond."}
            </p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-2 fade-up delay-4" data-testid="hero-pillars">
              {["Confidence", "Leadership", "Life Readiness"].map((pillar) => (
                <div key={pillar} className="flex items-center gap-2.5">
                  <span className="h-2 w-2 rounded-full bg-[#E94F9A]" />
                  <span className="font-serif text-lg sm:text-xl font-semibold text-[#4B1E5A]">{pillar}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 pt-4 fade-up delay-5">
              <button
                onClick={() => navigate("/partners")}
                data-testid="hero-btn-partner"
                className="bg-[#4B1E5A] text-white rounded-full px-8 py-4 font-sans text-sm font-semibold hover:bg-[#E94F9A] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              >
                {"Partner With AGG"}
              </button>
              <button
                onClick={() => navigate("/programs")}
                data-testid="hero-btn-programs"
                className="border border-[#4B1E5A] text-[#4B1E5A] rounded-full px-8 py-4 font-sans text-sm font-semibold hover:bg-[#FFF5F9] transition-all"
              >
                {"Explore Our Programs"}
              </button>
            </div>
          </div>

          <div className="lg:col-span-6 relative hero-image-in">
            <div className="img-zoom aspect-[4/5] rounded-[3rem] overflow-hidden shadow-2xl border-4 border-[#FFF5F9]/50 bg-[#FFF5F9]">
              <img 
                src={heroImage} 
                alt="A group of smiling girls embracing while wearing pink AGG branded shirts" 
                className="w-full h-full object-contain object-center"
              />
            </div>
            
            <div className="absolute -top-12 -left-12 w-48 h-48 rounded-full bg-[#FFF5F9] blur-3xl -z-10 opacity-60" />
            <div className="absolute -bottom-12 -right-12 w-64 h-64 rounded-full bg-[#4B1E5A]/10 blur-3xl -z-10 opacity-40" />
          </div>

        </div>
      </section>

      {/* SECTION 1.5: EMOTIONAL STATEMENT */}
      <section className="bg-[#4B1E5A] py-32 sm:py-44 px-6 overflow-hidden relative" data-testid="home-statement-section">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_20%,#E94F9A,transparent_55%)]" />
        <div className="max-w-4xl mx-auto text-center relative space-y-10">
          <span className="inline-block h-px w-16 bg-[#C8A96A] mx-auto" />
          <p className="font-serif text-3xl sm:text-4xl lg:text-[3rem] leading-[1.3] text-white font-medium italic">
            {"Every girl deserves someone who believes in her until she learns to believe in herself."}
          </p>
          <p className="font-sans text-sm sm:text-base tracking-[0.3em] uppercase text-[#C8A96A] font-semibold">
            {"That's why A Girl's Girl exists."}
          </p>
        </div>
      </section>

      {/* SECTION 2: WHO WE ARE */}
      <section className="py-24 bg-[#FFF5F9]/10 border-t border-[#FFF5F9]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
          
          <div className="md:col-span-5">
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-xl hover:scale-105 transition-all duration-500 max-w-sm mx-auto">
              <img 
                src={founderImg} 
                alt="Cherelle Hawkins-Frazier Founder" 
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4B1E5A]/40 to-transparent" />
            </div>
            <div className="text-center mt-4">
              <h4 className="font-serif text-lg font-bold text-[#4B1E5A]">{"Cherelle Hawkins-Frazier"}</h4>
              <p className="font-sans text-xs text-[#C8A96A] tracking-widest uppercase font-semibold">{"Founder & President"}</p>
            </div>
          </div>

          <div className="md:col-span-7 space-y-6 text-left">
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"FOUNDER'S HEART"}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] leading-snug agg-heading agg-heading-left">
              {"Every Girl Deserves to Thrive"}
            </h2>
            <p className="font-sans text-base text-[#4A4A4A] leading-relaxed italic">
              {"\"A Girl's Girl was founded after recognizing the growing need for girls to have safe spaces where they could build confidence, develop leadership skills, and prepare for life beyond the classroom...\""}
            </p>
            <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
              {"Through AGG, Cherelle is committed to creating transformational experiences that empower girls to walk confidently into their future—one girl, one school, and one community at a time."}
            </p>
            <div className="pt-4">
              <Link
                to="/founder"
                data-testid="home-link-meet-founder"
                className="inline-flex items-center gap-2 text-[#E94F9A] font-sans font-semibold hover:text-[#4B1E5A] transition-colors"
              >
                <span>{"Read Cherelle's Complete Story"}</span>
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 3: TRANSFORMATION JOURNEY (TIMELINE) */}
      <section className="py-24 bg-white border-t border-[#FFF5F9]">
        <div className="max-w-5xl mx-auto px-6 text-center space-y-16">
          <div className="space-y-4">
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"THE AGG DIFFERENCE"}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
              {"Every Girl Deserves the Opportunity to Thrive"}
            </h2>
            <p className="font-sans text-base text-[#4A4A4A] max-w-2xl mx-auto">
              {"AGG is more than a confidence program. We develop girls who are emotionally healthy, future-ready, purpose-driven, and prepared to lead—in school, in life, and in their communities."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            
            {/* Step 1: Before */}
            <div className="bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-3xl p-8 space-y-6 hover:shadow-md transition-all text-left">
              <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center border border-[#FFF5F9] shadow-sm">
                <span className="font-serif font-bold text-sm text-[#4A4A4A]">{"01"}</span>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#4B1E5A] uppercase tracking-wide">{"Before AGG"}</h3>
              <ul className="space-y-3 font-sans text-sm text-[#4A4A4A]">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                  {"Low self-esteem & confidence"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                  {"Afraid to speak up or share voice"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                  {"Limited exposure to success avenues"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                  {"Unsure of her potential & future"}
                </li>
              </ul>
            </div>

            {/* Step 2: During */}
            <div className="bg-[#E94F9A]/5 border border-[#E94F9A]/20 rounded-3xl p-8 space-y-6 hover:shadow-lg transition-all text-left relative overflow-hidden transform md:-translate-y-4">
              <div className="absolute top-0 right-0 bg-[#E94F9A] text-white text-[10px] px-3 py-1 rounded-bl-xl font-sans tracking-widest font-semibold uppercase">
                {"ACTIVE EXPERIENCES"}
              </div>
              <div className="h-10 w-10 rounded-full bg-[#E94F9A] flex items-center justify-center text-white shadow-md">
                <span className="font-serif font-bold text-sm">{"02"}</span>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#E94F9A] uppercase tracking-wide">{"The AGG Experience"}</h3>
              <ul className="space-y-3 font-sans text-sm text-[#4B1E5A]">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#E94F9A] flex-shrink-0" />
                  {"Confidence Development"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#E94F9A] flex-shrink-0" />
                  {"1-on-1 & Group Mentorship"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#E94F9A] flex-shrink-0" />
                  {"Practical Life Readiness Tasks"}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#E94F9A] flex-shrink-0" />
                  {"Sisterhood Circle support"}
                </li>
              </ul>
            </div>

            {/* Step 3: After */}
            <div className="bg-[#4B1E5A]/5 border border-[#4B1E5A]/10 rounded-3xl p-8 space-y-6 hover:shadow-md transition-all text-left">
              <div className="h-10 w-10 rounded-full bg-[#4B1E5A] flex items-center justify-center text-white shadow-sm">
                <span className="font-serif font-bold text-sm">{"03"}</span>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#4B1E5A] uppercase tracking-wide">{"After AGG"}</h3>
              <ul className="space-y-3 font-sans text-sm text-[#4A4A4A]">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C8A96A]" />
                  {"Walks boldly with high confidence"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C8A96A]" />
                  {"Purpose-driven & ready to lead"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C8A96A]" />
                  {"Equipped with life readiness skills"}
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C8A96A]" />
                  {"Ready to conquer high school, college & beyond"}
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 4: SIGNATURE PROGRAMS */}
      <section className="py-24 bg-[#FFF5F9]/10 border-t border-[#FFF5F9]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 space-y-16">
          <div className="text-center space-y-4">
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"SIGNATURE PROGRAMMING"}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
              {"Empowerment Designed for Every Stage"}
            </h2>
            <p className="font-sans text-base text-[#4A4A4A] max-w-2xl mx-auto">
              {"Our signature curriculums are carefully designed to foster high self-esteem, wellness, emotional growth, and critical life skills."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Program 1 */}
            <div className="bg-white rounded-3xl border border-[#E5E5E5]/50 shadow-sm p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 space-y-8 flex flex-col justify-between text-left min-h-[480px] group">
              <div className="space-y-4">
                <div className="h-12 w-10 bg-[#FFF5F9] rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <Sparkles className="h-6 w-6 text-[#4B1E5A]" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"SHE BELIEVES"}</h3>
                <p className="font-sans text-xs text-[#C8A96A] tracking-wider uppercase font-semibold">{"Confidence Development"}</p>
                <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
                  {"Helping girls discover their worth, find their distinct voice, and build leadership skills."}
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {["Confidence", "Leadership", "Communication", "Relationships"].map(t => (
                    <span key={t} className="bg-[#FFF5F9] text-[#E94F9A] text-[10px] px-2.5 py-1 rounded-full font-semibold font-sans">{t}</span>
                  ))}
                </div>
              </div>
              <button 
                onClick={() => navigate("/programs")}
                data-testid="program-explore-she-believes"
                className="inline-flex items-center gap-2 text-sm text-[#4B1E5A] font-semibold hover:text-[#E94F9A] transition-colors"
              >
                <span>{"Learn More"}</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Program 2 */}
            <div className="bg-white rounded-3xl border border-[#E5E5E5]/50 shadow-sm p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 space-y-8 flex flex-col justify-between text-left min-h-[480px] group">
              <div className="space-y-4">
                <div className="h-12 w-10 bg-[#FFF5F9] rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <Heart className="h-6 w-6 text-[#4B1E5A]" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"SHE GROWS"}</h3>
                <p className="font-sans text-xs text-[#C8A96A] tracking-wider uppercase font-semibold">{"Wellness & Self-Care"}</p>
                <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
                  {"Supporting the whole girl through emotional wellness, hygiene education, healthy boundaries, and self-care."}
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {["Period, Sis!", "Emotional Wellness", "Hygiene", "Self-Care"].map(t => (
                    <span key={t} className="bg-[#FFF5F9] text-[#E94F9A] text-[10px] px-2.5 py-1 rounded-full font-semibold font-sans">{t}</span>
                  ))}
                </div>
              </div>
              <button 
                onClick={() => navigate("/programs")}
                data-testid="program-explore-she-grows"
                className="inline-flex items-center gap-2 text-sm text-[#4B1E5A] font-semibold hover:text-[#E94F9A] transition-colors"
              >
                <span>{"Learn More"}</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Program 3 */}
            <div className="bg-white rounded-3xl border border-[#E5E5E5]/50 shadow-sm p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 space-y-8 flex flex-col justify-between text-left min-h-[480px] group">
              <div className="space-y-4">
                <div className="h-12 w-10 bg-[#FFF5F9] rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <Compass className="h-6 w-6 text-[#4B1E5A]" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"SHE LEADS"}</h3>
                <p className="font-sans text-xs text-[#C8A96A] tracking-wider uppercase font-semibold">{"Life Readiness"}</p>
                <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
                  {"Preparing girls for life beyond today through financial literacy, career exploration, and public speaking."}
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {["Financial Literacy", "Career Goals", "Public Speaking", "Etiquette"].map(t => (
                    <span key={t} className="bg-[#FFF5F9] text-[#E94F9A] text-[10px] px-2.5 py-1 rounded-full font-semibold font-sans">{t}</span>
                  ))}
                </div>
              </div>
              <button 
                onClick={() => navigate("/programs")}
                data-testid="program-explore-she-leads"
                className="inline-flex items-center gap-2 text-sm text-[#4B1E5A] font-semibold hover:text-[#E94F9A] transition-colors"
              >
                <span>{"Learn More"}</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 5: WHO WE SERVE & SCROLLING CAROUSEL */}
      <section className="py-24 bg-white border-t border-[#FFF5F9] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 text-center space-y-8 mb-12">
          <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"WHO WE SERVE"}</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
            {"Building Confident Girls. Strengthening Communities."}
          </h2>
          <p className="font-sans text-base text-[#4A4A4A] max-w-2xl mx-auto">
            {"From public schools to community organizations, foster care environments, and faith communities, we partner with groups dedicated to helping young girls grow."}
          </p>
        </div>
        <PartnerCarousel />
      </section>

      {/* SECTION 6: WHY AGG & WHY WE CARE */}
      <section className="py-24 bg-[#FFF5F9]/15 border-y border-[#FFF5F9]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          <div className="lg:col-span-6 space-y-6 text-left">
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"WHY AGG"}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] leading-snug agg-heading agg-heading-left">
              {"Creating Safe Spaces & Authentic Lifelong Skills"}
            </h2>
            <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
              {"We stand apart through our devotion to premium, personalized, life-readiness training. We build pathways that boost confidence, connect authentic female role models, and teach real-world skills."}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              <div className="flex gap-4 items-start">
                <Check className="h-5 w-5 text-[#E94F9A] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">{"Safe Supportive Space"}</h4>
                  <p className="font-sans text-xs text-[#4A4A4A] mt-0.5">{"Always welcoming, positive, and structured environment."}</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <Check className="h-5 w-5 text-[#E94F9A] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">{"Leadership Development"}</h4>
                  <p className="font-sans text-xs text-[#4A4A4A] mt-0.5">{"Equipping every girl to find her voice and lead with purpose."}</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <Check className="h-5 w-5 text-[#E94F9A] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">{"Vetted Mentors"}</h4>
                  <p className="font-sans text-xs text-[#4A4A4A] mt-0.5">{"Professional, trained women dedicated to helping girls succeed."}</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <Check className="h-5 w-5 text-[#E94F9A] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-serif font-bold text-sm text-[#4B1E5A]">{"Community Partnerships"}</h4>
                  <p className="font-sans text-xs text-[#4A4A4A] mt-0.5">{"Rooted in schools, churches, group homes, and youth organizations."}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 gap-6" data-testid="impact-pillars-grid">
            <div className="bg-white rounded-3xl p-8 border border-[#E5E5E5]/50 shadow-sm space-y-3 text-left">
              <div className="h-11 w-11 rounded-2xl bg-[#FFF5F9] flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#E94F9A]" /></div>
              <p className="font-serif font-bold text-base text-[#4B1E5A]">{"Confidence Development"}</p>
              <p className="font-sans text-xs text-[#4A4A4A]">{"Helping girls discover their worth and find their voice."}</p>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-[#E5E5E5]/50 shadow-sm space-y-3 text-left transform translate-y-6">
              <div className="h-11 w-11 rounded-2xl bg-[#FFF5F9] flex items-center justify-center"><Compass className="h-5 w-5 text-[#4B1E5A]" /></div>
              <p className="font-serif font-bold text-base text-[#4B1E5A]">{"Life Readiness"}</p>
              <p className="font-sans text-xs text-[#4A4A4A]">{"Practical skills for life beyond the classroom."}</p>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-[#E5E5E5]/50 shadow-sm space-y-3 text-left">
              <div className="h-11 w-11 rounded-2xl bg-[#FFF5F9] flex items-center justify-center"><Heart className="h-5 w-5 text-[#E94F9A]" /></div>
              <p className="font-serif font-bold text-base text-[#4B1E5A]">{"Emotional Wellness"}</p>
              <p className="font-sans text-xs text-[#4A4A4A]">{"Supporting the whole girl, mind and heart."}</p>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-[#E5E5E5]/50 shadow-sm space-y-3 text-left transform translate-y-6">
              <div className="h-11 w-11 rounded-2xl bg-[#FFF5F9] flex items-center justify-center"><Users className="h-5 w-5 text-[#4B1E5A]" /></div>
              <p className="font-serif font-bold text-base text-[#4B1E5A]">{"Mentorship"}</p>
              <p className="font-sans text-xs text-[#4A4A4A]">{"Trusted women who walk alongside every girl."}</p>
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 7 & 8: WHY ORGANIZATIONS PARTNER WITH AGG */}
      <section className="py-24 bg-white" data-testid="why-partner-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 space-y-16">
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"A TRUSTED PARTNER"}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
              {"Why Organizations Partner With AGG"}
            </h2>
            <p className="font-sans text-base text-[#4A4A4A]">
              {"We make it simple for schools, group homes, and community organizations to bring transformational programming to the girls they serve."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: BookOpen, title: "Ready-to-Deliver Curriculum", desc: "Thoughtfully designed programming that fits seamlessly into your existing schedule and space." },
              { icon: Users, title: "Vetted, Caring Mentors", desc: "Professional women trained to build genuine, trusted relationships with every girl." },
              { icon: Heart, title: "Whole-Girl Approach", desc: "We nurture confidence, wellness, and life readiness—not just one moment, but lasting growth." },
              { icon: Award, title: "Purpose-Built Framework", desc: "Every session is designed around the young woman each girl is becoming." }
            ].map((b, i) => (
              <div key={i} className="bg-[#FFF5F9]/40 border border-[#FFF5F9] rounded-3xl p-8 flex gap-5 items-start hover:shadow-md transition-all text-left">
                <div className="h-12 w-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <b.icon className={`h-5 w-5 ${i % 2 === 0 ? "text-[#E94F9A]" : "text-[#4B1E5A]"}`} />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-serif text-lg font-bold text-[#4B1E5A]">{b.title}</h3>
                  <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: THE FUTURE WE'RE BUILDING */}
      <section className="py-24 bg-gradient-to-br from-[#4B1E5A] to-[#321542] text-white" data-testid="future-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 text-center space-y-16">
          <div className="space-y-4">
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"OUR VISION"}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">
              {"The Future We're Building"}
            </h2>
            <p className="font-sans text-sm sm:text-base text-white/80 max-w-2xl mx-auto">
              {"We imagine a future where every girl—no matter her circumstances—has someone in her corner and a clear path toward confidence and leadership. We're building it through deep, lasting partnerships."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            {[
              { icon: BookOpen, title: "Schools", desc: "Bringing confidence circles and life-readiness workshops into classrooms." },
              { icon: Heart, title: "Group Homes & Foster Care", desc: "Creating safe, consistent spaces where every girl feels seen and supported." },
              { icon: Compass, title: "Churches & Community", desc: "Partnering with faith and community organizations to reach girls where they are." }
            ].map((f, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-4 hover:bg-white/10 transition-all">
                <div className="h-12 w-12 rounded-2xl bg-[#E94F9A]/20 flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-[#C8A96A]" />
                </div>
                <h4 className="font-serif font-bold text-lg">{f.title}</h4>
                <p className="font-sans text-sm text-white/70 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <p className="font-serif text-xl sm:text-2xl italic text-white/90 max-w-3xl mx-auto pt-4">
            {"Building Confident Girls. Strengthening Communities."}
          </p>
        </div>
      </section>

      {/* SECTION 9.5: SUPPORT AGG / JOIN THE MOVEMENT */}
      <SupportAGG />

      {/* SECTION 10: CALL TO ACTION */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8 bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-[3rem] p-12 md:p-16">
          <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#E94F9A]">{"GET INVOLVED"}</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
            {"Ready to Empower the Next Generation?"}
          </h2>
          <p className="font-sans text-base text-[#4A4A4A] max-w-xl mx-auto leading-relaxed">
            {"Whether you are a donor, volunteer, school administrator, or prospective mentor, your partnership can change a girl's entire trajectory."}
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <button
              onClick={() => navigate("/partners")}
              data-testid="cta-partner-btn"
              className="bg-[#4B1E5A] hover:bg-[#E94F9A] text-white rounded-full px-8 py-4 text-sm font-semibold transition-all shadow-md"
            >
              {"Partner With AGG"}
            </button>
            <button
              onClick={() => navigate("/mentor")}
              data-testid="cta-mentor-btn"
              className="bg-[#4B1E5A] hover:bg-[#E94F9A] text-white rounded-full px-8 py-4 text-sm font-semibold transition-all"
            >
              {"Become a Mentor"}
            </button>
            <DonateButton label="Donate Today" variant="outline" showTagline={false} className="px-8 py-4 text-sm" testId="cta-donate-btn" />
          </div>
        </div>
      </section>

    </div>
  );
};

// --- About Page ---
const AboutPage = () => {
  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="about-page">
      <div className="space-y-4">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"OUR MISSION"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Every Girl Deserves Someone Who Believes In Her"}
        </h1>
        <p className="font-serif text-xl sm:text-2xl text-[#E94F9A] italic">
          {"\"Where Confidence Meets Life Readiness.\""}
        </p>
      </div>

      <div className="img-zoom rounded-[3rem] overflow-hidden shadow-2xl border-4 border-[#FFF5F9]/50 aspect-[16/9]">
        <img src="https://static.prod-images.emergentagent.com/jobs/0e489a82-2f44-4016-bd13-c06ced2d5c32/images/03cd8aff8cb8f69a2cd9f1a1372b66f5e9dd8fab57f676954f936f7c7e73cc38.png" alt="A group of A Girl's Girl participants in black AGG branded shirts laughing together" className="w-full h-full object-cover object-center" loading="lazy" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
        <div className="space-y-6">
          <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"The AGG Vision"}</h3>
          <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
            {"A Girl's Girl equips girls with the confidence to thrive and the skills to lead through transformational programming, mentorship, life-skills education, leadership development, and supportive sisterhood."}
          </p>
          <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
            {"We target girls in underserved communities, foster care networks, and group homes, bridging the gap between standard education and life-readiness support."}
          </p>
        </div>
        <div className="bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-3xl p-8 space-y-6">
          <h4 className="font-serif text-lg font-bold text-[#4B1E5A] uppercase tracking-wider">{"Our Core Pillars"}</h4>
          <div className="space-y-4 font-sans text-sm text-[#4A4A4A]">
            <div className="flex gap-3">
              <Check className="h-5 w-5 text-[#E94F9A] flex-shrink-0" />
              <p><strong>{"Transformational Programming:"}</strong> {"Structured confidence building and personal hygiene workshops."}</p>
            </div>
            <div className="flex gap-3">
              <Check className="h-5 w-5 text-[#E94F9A] flex-shrink-0" />
              <p><strong>{"Professional Mentorship:"}</strong> {"Direct avenues connecting young girls to inspiring professional female guides."}</p>
            </div>
            <div className="flex gap-3">
              <Check className="h-5 w-5 text-[#E94F9A] flex-shrink-0" />
              <p><strong>{"Life Skills & Literacy:"}</strong> {"Practical etiquette, public speaking, financial skills, and leadership."}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#4B1E5A] text-white rounded-[3rem] p-12 space-y-6 text-center">
        <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#C8A96A]">{"The Future We're Building"}</h3>
        <p className="font-sans text-sm sm:text-base text-white/80 max-w-2xl mx-auto">
          {"We're focused on building meaningful, lasting partnerships with the schools, group homes, foster care programs, churches, and community organizations that serve girls every day."}
        </p>
      </div>
    </div>
  );
};

// --- Meet the Founder Page ---
const FounderPage = () => {
  const portraitUrl = "https://customer-assets.emergentagent.com/job_thrive-lead/artifacts/whnl4h7b_Cherelle%20Portrait%20%285%29.jpg";
  const portraitMugUrl = "https://customer-assets.emergentagent.com/job_thrive-lead/artifacts/fme160ev_Cherelle%20Portrait%20%282%29.jpg";

  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="founder-page">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
        
        <div className="md:col-span-5 space-y-4">
          <div className="rounded-[3rem] overflow-hidden shadow-xl border border-[#FFF5F9]">
            <img src={portraitUrl} alt="Cherelle Hawkins-Frazier Headshot" className="w-full h-auto object-cover" />
          </div>
          <div className="text-center">
            <h2 className="font-serif text-xl font-bold text-[#4B1E5A]">{"Cherelle Hawkins-Frazier"}</h2>
            <p className="font-sans text-xs text-[#C8A96A] tracking-widest font-semibold uppercase">{"Founder & President"}</p>
          </div>
        </div>

        <div className="md:col-span-7 space-y-6">
          <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#E94F9A]">{"MEET THE FOUNDER"}</span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#4B1E5A] leading-tight">
            {"Cherelle Hawkins-Frazier"}
          </h1>
          <p className="font-sans text-base text-[#4A4A4A] leading-relaxed italic">
            {"\"A Girl's Girl was founded by Cherelle Hawkins-Frazier after recognizing the growing need for girls to have safe spaces where they could build confidence, develop leadership skills, and prepare for life beyond the classroom.\""}
          </p>
          <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
            {"What began as a vision to encourage girls has evolved into a movement focused on confidence, mentorship, wellness, leadership, and life readiness—especially for girls in underserved communities, foster care, and group homes."}
          </p>
          <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
            {"Cherelle believes every girl deserves to know her worth, discover her purpose, and have access to opportunities that help her thrive. Through AGG, she is committed to creating transformational experiences that empower girls to walk confidently into their future."}
          </p>
        </div>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center bg-[#FFF5F9]/20 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-12">
        <div className="space-y-4">
          <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"The Future We're Building"}</h3>
          <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
            {"Under Cherelle's leadership, AGG is deepening its partnerships with schools, group homes, foster care organizations, churches, and community programs—creating more spaces where girls can grow into confident, capable young women."}
          </p>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-md max-w-sm mx-auto">
          <img src={portraitMugUrl} alt="Cherelle smiling with AGG mug" className="w-full h-auto object-cover" />
        </div>
      </div>
    </div>
  );
};

// --- Programs Page ---
const ProgramsPage = () => {
  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="programs-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"CURRICULUMS & WORKSHOPS"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Building Girls Who Believe In Themselves"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-2xl mx-auto">
          {"Providing a holistic approach to confidence building, self-care, and leadership preparation."}
        </p>
      </div>

      <div className="img-zoom rounded-[3rem] overflow-hidden shadow-2xl border-4 border-[#FFF5F9]/50 aspect-[16/9]">
        <img src="https://static.prod-images.emergentagent.com/jobs/0e489a82-2f44-4016-bd13-c06ced2d5c32/images/3a8953390313a2aee8bd76e4337096d0c08fde67b69eb5394c0d06aec3590a03.png" alt="Girls in AGG shirts creating vision boards and journaling together" className="w-full h-full object-cover object-center" loading="lazy" />
      </div>

      <div className="space-y-12">
        {/* SHE BELIEVES */}
        <div className="bg-white border border-[#E5E5E5]/50 shadow-sm rounded-3xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8 space-y-4">
            <div className="inline-block bg-[#FFF5F9] text-[#E94F9A] text-xs font-bold px-3 py-1 rounded-full font-sans tracking-widest uppercase">{"SHE BELIEVES"}</div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Confidence Development"}</h2>
            <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
              {"Helping girls discover their worth, find their voice, and become confident leaders. This curriculum walks participants through understanding internal value, body positivity, and sharing stories boldly."}
            </p>
            <div className="pt-2">
              <p className="font-serif font-bold text-sm text-[#4B1E5A] mb-2">{"Core Topics Covered:"}</p>
              <div className="flex flex-wrap gap-2 text-xs font-sans text-[#4A4A4A]">
                {["Self-worth", "Emotional Resilience", "Public Speaking", "Confidence Building"].map(t => (
                  <span key={t} className="bg-[#FFF5F9]/60 border border-[#E5E5E5]/30 rounded-full px-3 py-1">{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="md:col-span-4 bg-[#FFF5F9] rounded-2xl p-6 text-center space-y-4">
            <Sparkles className="h-10 w-10 text-[#E94F9A] mx-auto" />
            <p className="font-sans text-xs text-[#4A4A4A]">{"Designed for middle & high school girls"}</p>
            <Link to="/contact" className="bg-[#E94F9A] text-white block rounded-full py-2.5 text-xs font-semibold hover:bg-[#D13D86]">{"Inquire for School"}</Link>
          </div>
        </div>

        {/* SHE GROWS */}
        <div className="bg-white border border-[#E5E5E5]/50 shadow-sm rounded-3xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8 space-y-4">
            <div className="inline-block bg-[#FFF5F9] text-[#E94F9A] text-xs font-bold px-3 py-1 rounded-full font-sans tracking-widest uppercase">{"SHE GROWS"}</div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Wellness & Self-Care"}</h2>
            <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
              {"Supporting the whole girl through emotional wellness, hygiene education, healthy relationships, and positive self-care routines. Focuses heavily on periods, hygiene safety, and building healthy support networks."}
            </p>
            <div className="pt-2">
              <p className="font-serif font-bold text-sm text-[#4B1E5A] mb-2">{"Core Topics Covered:"}</p>
              <div className="flex flex-wrap gap-2 text-xs font-sans text-[#4A4A4A]">
                {["Period, Sis!", "Emotional Wellness", "Hygiene Safety", "Self-Care Routines"].map(t => (
                  <span key={t} className="bg-[#FFF5F9]/60 border border-[#E5E5E5]/30 rounded-full px-3 py-1">{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="md:col-span-4 bg-[#FFF5F9] rounded-2xl p-6 text-center space-y-4">
            <Heart className="h-10 w-10 text-[#E94F9A] mx-auto" />
            <p className="font-sans text-xs text-[#4A4A4A]">{"Includes free hygiene kit distribution"}</p>
            <Link to="/contact" className="bg-[#E94F9A] text-white block rounded-full py-2.5 text-xs font-semibold hover:bg-[#D13D86]">{"Request Kits"}</Link>
          </div>
        </div>

        {/* SHE LEADS */}
        <div className="bg-white border border-[#E5E5E5]/50 shadow-sm rounded-3xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8 space-y-4">
            <div className="inline-block bg-[#FFF5F9] text-[#E94F9A] text-xs font-bold px-3 py-1 rounded-full font-sans tracking-widest uppercase">{"SHE LEADS"}</div>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Life Readiness"}</h2>
            <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">
              {"Preparing girls for life beyond today through practical life skills, financial literacy, career exploration, etiquette, public speaking, and strategic goal setting."}
            </p>
            <div className="pt-2">
              <p className="font-serif font-bold text-sm text-[#4B1E5A] mb-2">{"Core Topics Covered:"}</p>
              <div className="flex flex-wrap gap-2 text-xs font-sans text-[#4A4A4A]">
                {["Financial Literacy", "Career Mentoring", "Etiquette & Polish", "Goal Planning"].map(t => (
                  <span key={t} className="bg-[#FFF5F9]/60 border border-[#E5E5E5]/30 rounded-full px-3 py-1">{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="md:col-span-4 bg-[#FFF5F9] rounded-2xl p-6 text-center space-y-4">
            <Compass className="h-10 w-10 text-[#E94F9A] mx-auto" />
            <p className="font-sans text-xs text-[#4A4A4A]">{"Prepares for college & career paths"}</p>
            <Link to="/contact" className="bg-[#E94F9A] text-white block rounded-full py-2.5 text-xs font-semibold hover:bg-[#D13D86]">{"Inquire for Group Home"}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Who We Serve Page ---
const WhoWeServePage = () => {
  const [formData, setFormData] = useState({
    organization: "",
    contact_name: "",
    email: "",
    phone: "",
    partner_type: "School",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partner`, formData);
      toast.success("Partnership request received! Cherelle and team will contact you soon.");
      setFormData({ organization: "", contact_name: "", email: "", phone: "", partner_type: "School", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="who-we-serve-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"WHO WE PARTNER WITH"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Who We Serve"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-2xl mx-auto">
          {"We bring transformational programming directly to spaces dedicated to girls' personal and emotional growth."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[
          { title: "School Districts & Schools", desc: "Interactive confidence circles, group mentoring, and assemblies during or after school." },
          { title: "Foster Care Programs", desc: "Holistic self-care kits, period coaching, and structured mentoring circles for youth." },
          { title: "Youth Group Homes", desc: "Hygiene kits, financial literacy lessons, and goal-setting systems." },
          { title: "Faith Communities", desc: "Weekend workshops, character mentoring, and supportive sisterhood camps." },
          { title: "Community Youth Programs", desc: "Customized workshops for girl scouts, sports associations, and youth networks." },
          { title: "Underserved Communities", desc: "Free accessible events for parents, mentors, and girls looking to walk boldly." }
        ].map((item, idx) => (
          <div key={idx} className="bg-white border border-[#E5E5E5]/50 rounded-3xl p-8 hover:shadow-md transition-all space-y-3">
            <h3 className="font-serif text-xl font-bold text-[#4B1E5A] flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-[#E94F9A]" />
              {item.title}
            </h3>
            <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Programming Inquiry Form */}
      <div className="bg-[#FFF5F9]/20 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-12 space-y-8">
        <div className="text-center space-y-2">
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Inquire About Hosting AGG"}</h3>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Fill out the form below to bring our transformational SHE BELIEVES, SHE GROWS, or SHE LEADS curriculum to your organization."}</p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto" data-testid="who-serve-inquiry-form">
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Organization Name"}</label>
            <input
              type="text"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
              placeholder="e.g. Charlotte Middle School"
              data-testid="input-who-serve-org"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Contact Person"}</label>
            <input
              type="text"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="e.g. Dr. Jane Smith"
              data-testid="input-who-serve-name"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Email Address"}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g. jsmith@schools.org"
              data-testid="input-who-serve-email"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Phone Number"}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 704-555-0122"
              data-testid="input-who-serve-phone"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Organization Type"}</label>
            <select
              value={formData.partner_type}
              onChange={(e) => setFormData({ ...formData, partner_type: e.target.value })}
              data-testid="select-who-serve-type"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
            >
              <option value="School">{"School / School District"}</option>
              <option value="Foster Care">{"Foster Care Organization"}</option>
              <option value="Group Home">{"Group Home"}</option>
              <option value="Church">{"Church / Faith-based Group"}</option>
              <option value="Community">{"Community Youth Organization"}</option>
              <option value="Other">{"Other"}</option>
            </select>
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Brief Message / Goals"}</label>
            <textarea
              rows="4"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about the girls you serve and how AGG can support them..."
              data-testid="textarea-who-serve-msg"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            ></textarea>
          </div>
          <div className="sm:col-span-2 text-center pt-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-who-serve"
              className="bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-full px-12 py-4 font-sans text-sm font-semibold transition-all shadow-md"
            >
              {loading ? "Submitting Inquiry..." : "Submit Hosting Request"}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

// --- Partners Page ---
const PartnersPage = () => {
  const [formData, setFormData] = useState({
    organization: "",
    contact_name: "",
    email: "",
    phone: "",
    partner_type: "Corporate Sponsor",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partner`, formData);
      toast.success("Partnership request received! Thank you for supporting A Girl's Girl.");
      setFormData({ organization: "", contact_name: "", email: "", phone: "", partner_type: "Corporate Sponsor", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="partners-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"GROW WITH US"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Partner With AGG"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-2xl mx-auto">
          {"We collaborate with corporate partners, grant organizations, foundations, and community advocates to create scalable transformation."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { level: "Bronze Sponsor", amount: "$5,000", benefits: "Funds wellness kits for 100 girls. Direct brand mention at events, logo on newsletter." },
          { level: "Silver Partner", amount: "$10,000", benefits: "Funds complete SHE LEADS readiness curriculum for one middle school. Recognition on site." },
          { level: "Gold Visionary", amount: "$25,000+", benefits: "Enables programmatic expansion in shelter/group homes. Executive spotlight & workshop panels." }
        ].map((level, idx) => (
          <div key={idx} className="bg-white border border-[#E5E5E5]/50 shadow-sm rounded-3xl p-8 hover:shadow-lg transition-all flex flex-col justify-between">
            <div className="space-y-4">
              <h3 className="font-serif text-xl font-bold text-[#4B1E5A]">{level.level}</h3>
              <p className="font-serif text-3xl font-extrabold text-[#E94F9A]">{level.amount}</p>
              <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{level.benefits}</p>
            </div>
            <div className="pt-6">
              <a href="#sponsor-form" className="bg-[#4B1E5A] text-white block text-center rounded-full py-2.5 text-xs font-semibold hover:bg-[#E94F9A]">{"Select Tier"}</a>
            </div>
          </div>
        ))}
      </div>

      <div id="sponsor-form" className="bg-[#FFF5F9]/20 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-12 space-y-8">
        <div className="text-center space-y-2">
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Corporate Inquiry Form"}</h3>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Learn how your organization can co-create workshops, sponsor modules, or provide grant funding."}</p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto" data-testid="partners-sponsor-form">
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Company / Foundation"}</label>
            <input
              type="text"
              value={formData.organization}
              onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
              placeholder="e.g. Bank of America"
              data-testid="partner-input-company"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Contact Person"}</label>
            <input
              type="text"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="e.g. Robert Vance"
              data-testid="partner-input-contact"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Email Address"}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g. rvance@bank.com"
              data-testid="partner-input-email"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Phone Number"}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 704-555-0100"
              data-testid="partner-input-phone"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Inquiry Type"}</label>
            <select
              value={formData.partner_type}
              onChange={(e) => setFormData({ ...formData, partner_type: e.target.value })}
              data-testid="partner-select-type"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
            >
              <option value="Corporate Sponsor">{"Corporate Sponsorship / Philanthropy"}</option>
              <option value="Foundation Grant">{"Foundation / Grant Reviewer"}</option>
              <option value="Individual Philanthropist">{"Major Donor / Advocate"}</option>
              <option value="Community Champion">{"Local Chapter Alliance"}</option>
            </select>
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Interest & Collaboration Detail"}</label>
            <textarea
              rows="4"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="How would your foundation or corporation like to support A Girl's Girl?"
              data-testid="partner-textarea-msg"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            ></textarea>
          </div>
          <div className="sm:col-span-2 text-center pt-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="partner-submit-btn"
              className="bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-full px-12 py-4 font-sans text-sm font-semibold transition-all shadow-md"
            >
              {loading ? "Sending Request..." : "Send Collaboration Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Become a Mentor Page ---
const BecomeAMentorPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    background: "",
    why_mentor: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/mentor`, formData);
      toast.success("Application received! Cherelle and the leadership team will review and connect with you.");
      setFormData({ first_name: "", last_name: "", email: "", phone: "", background: "", why_mentor: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 max-w-4xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="mentor-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"GUIDE & INSPIRE"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Be the Person Who Changes a Girl's Future"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-xl mx-auto">
          {"Help girls discover their self-worth, find their distinct voice, and prepare for successful futures by sharing your life experience."}
        </p>
      </div>

      <div className="img-zoom rounded-[3rem] overflow-hidden shadow-2xl border-4 border-[#FFF5F9]/50 aspect-[21/9]">
        <img src="https://static.prod-images.emergentagent.com/jobs/0e489a82-2f44-4016-bd13-c06ced2d5c32/images/90db06f1d42ce7a9bb838db9e35b637fec2b07ecf689b9bb985a29bb29033299.png" alt="An AGG mentor encouraging a girl in a warm one-on-one conversation" className="w-full h-full object-cover object-center" loading="lazy" />
      </div>

      <div className="bg-[#FFF5F9]/20 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-12 space-y-8">
        <div className="text-center space-y-2">
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Mentor Application"}</h3>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Submit your details below. All mentors are thoroughly vetted and trained prior to circle matching."}</p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto" data-testid="mentor-app-form">
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"First Name"}</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="e.g. Jessica"
              data-testid="mentor-input-firstname"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Last Name"}</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="e.g. Carter"
              data-testid="mentor-input-lastname"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Email Address"}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g. jessica.c@example.com"
              data-testid="mentor-input-email"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Phone Number"}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 704-555-0199"
              data-testid="mentor-input-phone"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Professional & Educational Background"}</label>
            <textarea
              rows="3"
              value={formData.background}
              onChange={(e) => setFormData({ ...formData, background: e.target.value })}
              placeholder="What is your current field of work, education background, or leadership experience?"
              data-testid="mentor-textarea-background"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            ></textarea>
          </div>
          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Why do you want to mentor with A Girl's Girl?"}</label>
            <textarea
              rows="3"
              value={formData.why_mentor}
              onChange={(e) => setFormData({ ...formData, why_mentor: e.target.value })}
              placeholder="Tell us about your motivation for joining our mentorship network..."
              data-testid="mentor-textarea-why"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            ></textarea>
          </div>
          <div className="sm:col-span-2 text-center pt-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="mentor-submit-btn"
              className="bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-full px-12 py-4 font-sans text-sm font-semibold transition-all shadow-md"
            >
              {loading ? "Submitting Application..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Volunteer Page ---
const VolunteerPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    interest_areas: [],
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleCheckboxChange = (area) => {
    const nextAreas = formData.interest_areas.includes(area)
      ? formData.interest_areas.filter(a => a !== area)
      : [...formData.interest_areas, area];
    setFormData({ ...formData, interest_areas: nextAreas });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, formData);
      toast.success("Volunteer interest received! We are so excited to have you in our community circle.");
      setFormData({ first_name: "", last_name: "", email: "", phone: "", interest_areas: [], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 max-w-4xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="volunteer-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"JOIN THE SISTERHOOD"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Be the Reason a Girl Believes She Can"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-xl mx-auto">
          {"Help coordinate events, distribute hygiene kits, organize workshops, and support the operations of A Girl's Girl."}
        </p>
      </div>

      <div className="img-zoom rounded-[3rem] overflow-hidden shadow-2xl border-4 border-[#FFF5F9]/50 aspect-[21/9]">
        <img src="https://static.prod-images.emergentagent.com/jobs/0e489a82-2f44-4016-bd13-c06ced2d5c32/images/ca96aeccffc98bc6a564a2258e79ae44f45e043398dbbd4306290ba57302759a.png" alt="A confident girl in an AGG shirt speaking and leading her peers" className="w-full h-full object-cover object-center" loading="lazy" />
      </div>

      <div className="bg-[#FFF5F9]/20 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-12 space-y-8">
        <div className="text-center space-y-2">
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Volunteer Application"}</h3>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Fill out your details and areas of interest. We offer highly flexible event-based scheduling."}</p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto" data-testid="volunteer-app-form">
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"First Name"}</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              placeholder="e.g. Maya"
              data-testid="volunteer-input-firstname"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Last Name"}</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              placeholder="e.g. Rodriguez"
              data-testid="volunteer-input-lastname"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Email Address"}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g. maya.r@example.com"
              data-testid="volunteer-input-email"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Phone Number"}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 704-555-0143"
              data-testid="volunteer-input-phone"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>

          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Areas of Interest"}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1 font-sans text-sm text-[#4A4A4A]">
              {[
                "SHE BELIEVES Workshops",
                "SHE GROWS Self-Care Events",
                "Hygiene Kit Assembly & Distribution",
                "Administrative & Social Media Support",
                "Event Photography & Videography",
                "Fundraising & Grants Support"
              ].map(area => {
                const checked = formData.interest_areas.includes(area);
                return (
                  <label key={area} className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleCheckboxChange(area)}
                      data-testid={`volunteer-check-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="rounded border-[#E5E5E5] text-[#E94F9A] focus:ring-[#E94F9A] h-4.5 w-4.5"
                    />
                    <span>{area}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:col-span-2">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Tell us why you'd like to volunteer"}</label>
            <textarea
              rows="3"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Share a bit about your experiences or why you'd like to support A Girl's Girl..."
              data-testid="volunteer-textarea-msg"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            ></textarea>
          </div>
          <div className="sm:col-span-2 text-center pt-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="volunteer-submit-btn"
              className="bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-full px-12 py-4 font-sans text-sm font-semibold transition-all shadow-md"
            >
              {loading ? "Submitting Application..." : "Submit Volunteer Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Donate Page with real Stripe polling returning status check ---
const DonatePage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const checkoutStatus = searchParams.get("status");

  const [loading, setLoading] = useState(false);
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [selectedTier, setSelectedTier] = useState("tier_three"); // Default $100

  // Polling states
  const [polling, setPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState("");
  const [pollResult, setPollResult] = useState(null);

  const tiers = [
    { id: "tier_one", name: "Friend of AGG", amount: 25, desc: "Provides essential learning notebooks & folders for girls." },
    { id: "tier_two", name: "Confidence Sponsor", amount: 50, desc: "Sponsors complete self-care kits & wellness cards." },
    { id: "tier_three", name: "Sisterhood Leader", amount: 100, desc: "Funds direct career mentorship support circles." },
    { id: "tier_four", name: "Life Readiness Champion", amount: 250, desc: "Sponsors leadership curriculum books & modules." },
    { id: "tier_five", name: "Empowerment Partner", amount: 500, desc: "Sponsors workshop facilities and healthy snacks for 5 girls." },
    { id: "tier_six", name: "Transformation Champion", amount: 1000, desc: "Enables scholarship, mentoring, and support." }
  ];

  // Poll function to check donation status from backend
  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPolling(false);
      setPollMessage("Payment verification status check timed out. Please check your email for receipt.");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sid}`);
      const data = response.data;
      
      if (data.payment_status === "paid") {
        setPolling(false);
        setPollResult({ success: true, amount: data.amount, donor: data.donor_name });
        toast.success("Thank you! Your donation was successful.");
      } else {
        setPollMessage("Payment is being verified by Stripe secure systems...");
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error polling payment status:", error);
      setPolling(false);
      setPollMessage("Error verifying payment status with server.");
    }
  };

  useEffect(() => {
    if (sessionId && checkoutStatus === "success") {
      setPolling(true);
      setPollMessage("Connecting securely to Stripe to verify your donation...");
      pollPaymentStatus(sessionId);
    } else if (checkoutStatus === "cancel") {
      toast.info("Donation process was cancelled. You can try again when ready.");
    }
  }, [sessionId, checkoutStatus]);

  const handleDonate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout/session`, {
        package_id: selectedTier,
        origin_url: window.location.origin,
        donor_name: donorName || "Anonymous",
        donor_email: donorEmail || ""
      });

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error("Invalid response from payment server.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="donate-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"CHAMPION THE FUTURE"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Your Gift Becomes Her Future"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-xl mx-auto">
          {"Help fund workshops, curriculums, and hygiene kits. Your tax-deductible gift empowers the next generation of female leaders."}
        </p>
      </div>

      {/* STRIPE RETURN VERIFICATION MODAL / DIALOG IN-PAGE VIEW */}
      {polling && (
        <div className="bg-[#FFF5F9]/30 border-2 border-dashed border-[#E94F9A]/40 rounded-3xl p-8 text-center space-y-4 max-w-xl mx-auto" data-testid="payment-polling-banner">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E94F9A] mx-auto"></div>
          <h3 className="font-serif text-xl font-bold text-[#4B1E5A]">{"Verifying Donation"}</h3>
          <p className="font-sans text-sm text-[#4A4A4A]">{pollMessage}</p>
        </div>
      )}

      {pollResult && pollResult.success && (
        <div className="bg-[#E94F9A]/10 border border-[#E94F9A]/30 rounded-3xl p-8 md:p-12 text-center space-y-6 max-w-xl mx-auto" data-testid="payment-success-card">
          <div className="h-16 w-16 bg-[#E94F9A] rounded-full flex items-center justify-center mx-auto shadow-md">
            <Check className="h-8 w-8 text-white" />
          </div>
          <div className="space-y-2">
            <h2 className="font-serif text-3xl font-bold text-[#4B1E5A]">{"Thank You, "}{pollResult.donor}{"!"}</h2>
            <p className="font-serif text-[#C8A96A] text-xl font-semibold">{"Your Gift of $"}{pollResult.amount}{" Makes a True Difference."}</p>
            <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed max-w-sm mx-auto">
              {"Your donation transaction has been securely processed. A tax-receipt has been sent to your email. Thank you for empowering A Girl's Girl."}
            </p>
          </div>
          <button
            onClick={() => {
              setPollResult(null);
              window.history.replaceState({}, document.title, window.location.pathname);
            }}
            data-testid="success-clear-btn"
            className="bg-[#4B1E5A] text-white px-8 py-3 rounded-full text-xs font-semibold hover:bg-[#E94F9A] transition-all"
          >
            {"Support Again / Close"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        
        {/* Support Levels on Left */}
        <div className="lg:col-span-7 space-y-4">
          <h3 className="font-serif text-xl font-bold text-[#4B1E5A] mb-4">{"Choose a Level of Support"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {tiers.map((tier) => {
              const isSelected = selectedTier === tier.id;
              return (
                <div
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  data-testid={`donation-tier-${tier.amount}`}
                  className={`border rounded-2xl p-5 text-left cursor-pointer transition-all ${
                    isSelected 
                      ? "border-[#E94F9A] bg-[#FFF5F9]/20 shadow-md ring-2 ring-[#E94F9A]/30" 
                      : "border-[#E5E5E5]/50 bg-white hover:border-[#E94F9A]/30 hover:shadow-sm"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <p className="font-serif text-sm font-semibold text-[#4B1E5A]">{tier.name}</p>
                    {isSelected && <Check className="h-4 w-4 text-[#E94F9A]" />}
                  </div>
                  <p className="font-serif text-2xl font-bold text-[#E94F9A] mt-2">${tier.amount}</p>
                  <p className="font-sans text-[11px] text-[#4A4A4A] mt-1.5 leading-relaxed">{tier.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Secure Form on Right */}
        <div className="lg:col-span-5 bg-white border border-[#E5E5E5]/50 shadow-md rounded-3xl p-8 space-y-6">
          <div className="text-left space-y-1">
            <h3 className="font-serif text-lg font-bold text-[#4B1E5A]">{"Secure Donation"}</h3>
            <p className="font-sans text-xs text-[#4A4A4A]">{"Payments are processed securely via Stripe Test Systems."}</p>
          </div>

          <form onSubmit={handleDonate} className="space-y-4 text-left" data-testid="donation-payment-form">
            <div className="flex flex-col">
              <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wider">{"Your Name (Optional)"}</label>
              <input
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder="e.g. Annette Jenkins"
                data-testid="donate-input-name"
                className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm"
              />
            </div>
            <div className="flex flex-col">
              <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wider">{"Email Address (Optional)"}</label>
              <input
                type="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                placeholder="e.g. annette@example.com"
                data-testid="donate-input-email"
                className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm"
              />
            </div>

            <div className="bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-xl p-4 space-y-2">
              <div className="flex justify-between items-center text-sm font-semibold text-[#4B1E5A]">
                <span>{"Selected Support:"}</span>
                <span>{tiers.find(t => t.id === selectedTier)?.name}</span>
              </div>
              <div className="flex justify-between items-center text-lg font-bold text-[#E94F9A]">
                <span>{"Total Gift:"}</span>
                <span>{"$"}{tiers.find(t => t.id === selectedTier)?.amount}{".00 USD"}</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="btn-initiate-donation"
              className="w-full bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-xl py-3.5 text-sm font-semibold transition-all shadow-md flex items-center justify-center gap-2"
            >
              <Gift className="h-4 w-4" />
              <span>{loading ? "Initiating Secure Stripe..." : "Initiate Donation via Stripe"}</span>
            </button>
          </form>

          <div className="text-center">
            <p className="text-[10px] text-white/50 bg-[#4B1E5A] py-2 rounded-lg font-sans">
              {"🔒 TAX-DEDUCTIBLE 501(C)(3) INITIATIVE"}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

// --- Events Page ---
const EventsPage = () => {
  const [formData, setFormData] = useState({
    event_id: "",
    event_title: "",
    name: "",
    email: "",
    phone: "",
    quantity: 1
  });
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content/events`).then((r) => {
      setEvents(r.data || []);
      if (r.data && r.data.length > 0) {
        setFormData((f) => ({ ...f, event_id: r.data[0].id, event_title: r.data[0].title }));
      }
    }).catch(() => {});
  }, []);

  const experiences = [
    { icon: GraduationCap, title: "College Tours", desc: "Stepping onto a campus and realizing—this could be me." },
    { icon: Star, title: "Leadership Days", desc: "Finding her voice and learning to lead with courage." },
    { icon: Sparkles, title: "Confidence Workshops", desc: "Discovering her worth and learning to believe in herself." },
    { icon: Heart, title: "Glow Up Days", desc: "Celebrating self-care, wellness, and inner beauty." },
    { icon: Compass, title: "Summer Leadership Camp", desc: "A week of growth, sisterhood, and unforgettable memories." },
    { icon: Award, title: "Scholarship Nights", desc: "Opening doors to a future she can picture clearly." },
    { icon: MessageCircle, title: "Mentor Meetups", desc: "One-on-one encouragement from women who believe in her." },
    { icon: Rocket, title: "Entrepreneurship Labs", desc: "Turning big ideas into real plans and bold action." },
    { icon: Briefcase, title: "Career Exploration", desc: "Imagining all the futures that could be hers." },
    { icon: Users, title: "Service Projects", desc: "Leading with compassion and giving back to her community." },
    { icon: Calendar, title: "Community Events", desc: "Belonging to something bigger than herself." },
    { icon: Target, title: "Goal-Setting Workshops", desc: "Mapping the path from who she is to who she's becoming." }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        event_id: formData.event_id || "general-interest",
        event_title: formData.event_title || "Upcoming AGG Experience",
      };
      await axios.post(`${API}/events/register`, payload);
      toast.success("RSVP Received! An email confirmation has been sent to you.");
      setFormData((f) => ({ ...f, name: "", email: "", phone: "", quantity: 1 }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-28 max-w-6xl mx-auto px-6 text-left space-y-24 animate-fade-in" data-testid="events-page">
      <div className="space-y-5 text-center max-w-2xl mx-auto">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"EXPERIENCES & OPPORTUNITIES"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
          {"Where Girls Discover What They're Capable Of"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A]">
          {"Every AGG experience is a doorway—to confidence, to leadership, to a future she can see clearly. These are the moments a girl remembers for a lifetime."}
        </p>
      </div>

      <div className="img-zoom rounded-[3rem] overflow-hidden shadow-2xl border-4 border-[#FFF5F9]/50 aspect-[21/9]">
        <img src="https://static.prod-images.emergentagent.com/jobs/0e489a82-2f44-4016-bd13-c06ced2d5c32/images/718b205a6d10a265abd1d60c8de25016ba9fedfa97b961278cd04c23852751e5.png" alt="Girls in AGG shirts celebrating joyfully together" className="w-full h-full object-cover object-center" loading="lazy" />
      </div>

      {/* Experiences to look forward to */}
      <div className="space-y-12">
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A] text-center">{"Experiences to Look Forward To"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="experiences-grid">
          {experiences.map((x, i) => (
            <div key={i} className="group bg-white border border-[#E5E5E5]/50 rounded-3xl p-7 space-y-3 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 text-left">
              <div className="h-12 w-12 rounded-2xl bg-[#FFF5F9] flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <x.icon className={`h-5 w-5 ${i % 2 === 0 ? "text-[#E94F9A]" : "text-[#4B1E5A]"}`} />
              </div>
              <h3 className="font-serif text-lg font-bold text-[#4B1E5A]">{x.title}</h3>
              <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{x.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-10">
        <div className="space-y-3 text-center max-w-xl mx-auto">
          <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"MARK YOUR CALENDAR"}</span>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Upcoming Experiences"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"All experiences are free for girls and simply require an RSVP so we can save her a seat."}</p>
        </div>

      <div className="grid grid-cols-1 gap-8 max-w-3xl mx-auto">
        {events.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5]/50 shadow-sm rounded-3xl p-12 text-center space-y-4" data-testid="events-coming-soon">
            <Calendar className="h-12 w-12 text-[#E94F9A] mx-auto" />
            <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"Events Coming Soon"}</h3>
            <p className="font-sans text-sm text-[#4A4A4A] max-w-md mx-auto">{"New confidence circles, leadership days, and experiences are on the way. Check back soon — or RSVP below to be first to know."}</p>
          </div>
        ) : (
          events.map((evt) => (
            <div key={evt.id} data-testid={`event-card-${evt.id}`} className="bg-white border border-[#E5E5E5]/50 shadow-sm rounded-3xl p-8 hover:shadow-md transition-all flex flex-col md:flex-row justify-between gap-6">
              <div className="space-y-3 max-w-xl">
                <h3 className="font-serif text-xl font-bold text-[#4B1E5A]">{evt.title}</h3>
                <div className="flex flex-wrap gap-4 text-xs font-sans text-[#4A4A4A]">
                  {evt.date_label && <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[#E94F9A]" /> {evt.date_label}</span>}
                  {evt.time_label && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#E94F9A]" /> {evt.time_label}</span>}
                  {evt.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#E94F9A]" /> {evt.location}</span>}
                </div>
                {evt.description && <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed pt-1">{evt.description}</p>}
              </div>
              <div className="flex items-center justify-center">
                <button
                  onClick={() => { setFormData({ ...formData, event_id: evt.id, event_title: evt.title }); document.getElementById("event-rsvp-form")?.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid={`event-select-btn-${evt.id}`}
                  className="bg-[#FFF5F9] text-[#E94F9A] rounded-full px-6 py-2.5 text-xs font-semibold hover:bg-[#E94F9A] hover:text-white transition-all whitespace-nowrap"
                >
                  {"RSVP / Select"}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      </div>

      {/* Registration Form */}
      <div id="event-rsvp-form" className="bg-[#FFF5F9]/20 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-12 space-y-6 max-w-3xl mx-auto scroll-mt-28">
        <div className="text-center space-y-1">
          <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"Register For Event"}</h3>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Selected: "}<strong className="text-[#E94F9A]">{formData.event_title || "Upcoming AGG Experience"}</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="event-registration-form">
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Full Name"}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Maya Lin"
              data-testid="event-input-name"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Email Address"}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g. maya@example.com"
              data-testid="event-input-email"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Phone Number"}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g. 704-555-0100"
              data-testid="event-input-phone"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="font-sans text-xs font-semibold text-[#4A4A4A] mb-1.5 uppercase tracking-wider">{"Number of Attendees"}</label>
            <input
              type="number"
              min="1"
              max="5"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
              data-testid="event-input-quantity"
              className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>
          <div className="sm:col-span-2 text-center pt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="event-submit-btn"
              className="bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-full px-12 py-3.5 font-sans text-sm font-semibold transition-all shadow-md"
            >
              {loading ? "Registering..." : "Confirm RSVP"}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

// --- Impact Page ---
const ImpactPage = () => {
  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="impact-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"OUR IMPACT PILLARS"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"The Difference AGG Makes"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-xl mx-auto">
          {"As an emerging nonprofit, our impact is measured in transformation—who a girl becomes through confidence, mentorship, and life readiness."}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8" data-testid="impact-pillar-cards">
        {[
          { icon: Sparkles, title: "Confidence Development", desc: "Helping girls discover their worth and find their voice." },
          { icon: Award, title: "Leadership Development", desc: "Building the courage to speak up, step forward, and lead." },
          { icon: Compass, title: "Life Readiness", desc: "Practical skills for life beyond the classroom." },
          { icon: Users, title: "Mentorship", desc: "Trusted women who walk alongside every girl." },
          { icon: Heart, title: "Emotional Wellness", desc: "Caring for the whole girl—mind, heart, and spirit." },
          { icon: Building, title: "Community Partnerships", desc: "Rooted in schools, churches, group homes, and youth programs." }
        ].map((p, i) => (
          <div key={i} className="bg-white border border-[#E5E5E5]/50 rounded-3xl p-8 text-center space-y-3 hover:shadow-md transition-all">
            <div className="h-12 w-12 rounded-full bg-[#FFF5F9] flex items-center justify-center mx-auto">
              <p.icon className={`h-6 w-6 ${i % 2 === 0 ? "text-[#E94F9A]" : "text-[#4B1E5A]"}`} />
            </div>
            <h4 className="font-serif font-bold text-base text-[#4B1E5A]">{p.title}</h4>
            <p className="font-sans text-xs text-[#4A4A4A] leading-relaxed">{p.desc}</p>
          </div>
        ))}
        <div className="bg-[#4B1E5A] rounded-3xl p-8 text-center space-y-3 flex flex-col items-center justify-center">
          <div className="h-12 w-12 rounded-full bg-[#E94F9A]/20 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-6 w-6 text-[#C8A96A]" />
          </div>
          <h4 className="font-serif font-bold text-base text-white">{"Safe Spaces"}</h4>
          <p className="font-sans text-xs text-white/70 leading-relaxed">{"Welcoming, consistent environments where every girl belongs."}</p>
        </div>
      </div>

      <div className="bg-[#4B1E5A] text-white rounded-[3rem] p-12 space-y-8">
        <div className="text-center space-y-2">
          <span className="font-sans text-xs text-[#C8A96A] uppercase tracking-widest font-semibold">{"OUR VISION"}</span>
          <h3 className="font-serif text-2xl sm:text-3xl font-bold">{"The Future We're Building"}</h3>
          <p className="font-sans text-sm text-white/80 max-w-xl mx-auto">{"We're growing thoughtfully—deepening partnerships and strengthening our programming so every girl we serve walks away more confident and ready for life."}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left font-sans text-sm">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-2">
            <h4 className="font-serif font-bold text-sm text-[#C8A96A]">{"Deepening Partnerships"}</h4>
            <p className="text-white/70 text-xs leading-relaxed">{"Working hand-in-hand with schools, group homes, foster care programs, and churches."}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-2">
            <h4 className="font-serif font-bold text-sm text-[#C8A96A]">{"Strengthening Programming"}</h4>
            <p className="text-white/70 text-xs leading-relaxed">{"Continually refining our SHE BELIEVES, SHE GROWS, and SHE LEADS experiences."}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Contact Page ---
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState({ email: "info@shesagirlsgirl.org", phone: "980.406.5940", address: "Charlotte, North Carolina" });

  useEffect(() => {
    axios.get(`${API}/content-settings`).then((r) => {
      const d = r.data || {};
      setInfo((i) => ({
        email: d.contact_email || i.email,
        phone: d.contact_phone || i.phone,
        address: d.contact_address || i.address,
      }));
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! Cherelle Frazier and the AGG team will get back to you.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-16 animate-fade-in" data-testid="contact-page">
      <div className="space-y-4 text-center">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"GET IN TOUCH"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Contact Us"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] max-w-xl mx-auto">
          {"Have questions about our signature confidence programs, wellness kit distributions, or partnership tiers? We'd love to connect."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
        
        {/* Info Column */}
        <div className="md:col-span-5 space-y-8 font-sans text-[#4A4A4A] text-sm">
          <h3 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"Administrative Offices"}</h3>
          <p className="leading-relaxed">{"A Girl's Girl is headquartered in Charlotte, North Carolina. We coordinate programs throughout local school districts and foster care homes."}</p>
          
          <div className="space-y-4 pt-2">
            <div className="flex gap-3 items-center">
              <div className="h-9 w-9 rounded-full bg-[#FFF5F9] flex items-center justify-center text-[#E94F9A] flex-shrink-0">
                <Mail className="h-4.5 w-4.5" />
              </div>
              <a href={`mailto:${info.email}`} data-testid="contact-email" className="font-medium text-[#4B1E5A] hover:text-[#E94F9A]">{info.email}</a>
            </div>
            <div className="flex gap-3 items-center">
              <div className="h-9 w-9 rounded-full bg-[#FFF5F9] flex items-center justify-center text-[#E94F9A] flex-shrink-0">
                <Phone className="h-4.5 w-4.5" />
              </div>
              <a href={`tel:${info.phone.replace(/[^0-9+]/g, "")}`} data-testid="contact-phone" className="font-medium text-[#4B1E5A] hover:text-[#E94F9A]">{info.phone}</a>
            </div>
          </div>

          <div className="bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-3xl p-6">
            <h4 className="font-serif font-bold text-sm text-[#4B1E5A] mb-1">{"Donor Transparency"}</h4>
            <p className="text-xs leading-relaxed">{"A Girl's Girl is a registered 501(c)(3) nonprofit organization. All donations are tax-deductible to the full extent of the law."}</p>
          </div>
        </div>

        {/* Contact Form Column */}
        <div className="md:col-span-7 bg-[#FFF5F9]/20 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-10 space-y-6">
          <h3 className="font-serif text-xl font-bold text-[#4B1E5A]">{"Send an Inquiry Message"}</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wider">{"Full Name"}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Linda Jacobs"
                data-testid="contact-input-name"
                className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm"
                required
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wider">{"Email Address"}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="e.g. linda@example.com"
                data-testid="contact-input-email"
                className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm"
                required
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wider">{"Subject"}</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g. Shelter programming inquiry"
                data-testid="contact-input-subject"
                className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm"
                required
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-[#4A4A4A] mb-1 uppercase tracking-wider">{"Your Message"}</label>
              <textarea
                rows="4"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="How can we help you or collaborate?"
                data-testid="contact-textarea-msg"
                className="bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm"
                required
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="w-full bg-[#E94F9A] hover:bg-[#D13D86] text-white rounded-xl py-3 font-semibold transition-all shadow-md"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

// --- Admin Dashboard (Seeded with beautiful initial tabs, status updates, and dynamic export) ---

// --- Imagine Her: site-wide transformation section with a unique CTA per page ---
const IMAGINE_CTA = {
  "/": { label: "Explore Our Programs", path: "/programs" },
  "/about": { label: "Meet Our Founder", path: "/founder" },
  "/founder": { label: "Explore Our Programs", path: "/programs" },
  "/programs": { label: "Join a Program", path: "/contact" },
  "/who-we-serve": { label: "Partner With AGG", path: "/partners" },
  "/partners": { label: "Partner With AGG", path: "/partners" },
  "/mentor": { label: "Become a Mentor", path: "/mentor" },
  "/volunteer": { label: "Become a Mentor", path: "/mentor" },
  "/events": { label: "Reserve Your Spot", path: "/events" },
  "/impact": { label: "Support Our Mission", path: "/donate" },
  "/contact": { label: "Let's Start the Conversation", path: "/contact" }
};

const IMAGINE_TRAITS = [
  "Confident in who she is",
  "Speaking with courage",
  "Leading others with compassion",
  "Building healthy relationships",
  "Planning for college and career",
  "Serving her community",
  "Mentoring younger girls",
  "Setting goals for her future",
  "Walking with purpose",
  "Believing in herself"
];

const ImagineHer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const cta = IMAGINE_CTA[location.pathname] || IMAGINE_CTA["/"];
  const imageUrl = "https://static.prod-images.emergentagent.com/jobs/0e489a82-2f44-4016-bd13-c06ced2d5c32/images/14de56cc1c28e31898362d63095c0e0d6909262edd4c3efa0a0d6f9c8792f004.png";

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [location.pathname]);

  return (
    <section
      ref={ref}
      data-testid="imagine-her-section"
      className={`bg-[#FFF5F9]/50 py-28 sm:py-40 px-6 sm:px-12 lg:px-24 ${visible ? "is-visible" : ""}`}
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5 imagine-portrait">
          <div className="img-zoom aspect-[4/5] rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white">
            <img src={imageUrl} alt="A confident girl in a black AGG shirt standing tall and smiling with warm eye contact" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
        </div>
        <div className="lg:col-span-7 space-y-8">
          <div className="space-y-4 imagine-head">
            <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"THE TRANSFORMATION"}</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4B1E5A] agg-heading agg-heading-left">
              {"Imagine Her One Year From Now"}
            </h2>
            <p className="font-sans text-base sm:text-lg text-[#4A4A4A] max-w-xl leading-relaxed">
              {"At A Girl's Girl, transformation isn't measured only by what girls learn—it's reflected in who they become."}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
            {IMAGINE_TRAITS.map((t, i) => (
              <div
                key={i}
                className="imagine-trait flex items-center gap-3"
                style={{ animationDelay: `${0.15 + i * 0.07}s` }}
              >
                <div className="h-7 w-7 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Check className={`h-4 w-4 ${i % 2 === 0 ? "text-[#E94F9A]" : "text-[#4B1E5A]"}`} />
                </div>
                <span className="font-sans text-sm sm:text-base text-[#2F2F2F] font-medium">{t}</span>
              </div>
            ))}
          </div>
          <p className="font-serif text-xl sm:text-2xl italic text-[#4B1E5A] pt-2">
            {"Because confidence changes everything."}
          </p>
          <button
            onClick={() => navigate(cta.path)}
            data-testid="imagine-her-cta"
            className="group inline-flex items-center gap-2 bg-[#4B1E5A] hover:bg-[#E94F9A] text-white rounded-full px-8 py-4 font-sans text-sm font-semibold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
          >
            {cta.label}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

// --- Our Promise: emotional pre-footer band ---
const OurPromise = () => {
  return (
    <section className="bg-white py-24 px-6" data-testid="our-promise-section">
      <div className="max-w-3xl mx-auto text-center space-y-6">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"OUR PROMISE"}</span>
        <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#4B1E5A] leading-snug font-medium">
          {"We believe every girl deserves the confidence to believe in herself, the courage to lead, and the opportunity to thrive."}
        </p>
        <span className="inline-block h-1 w-16 rounded-full bg-gradient-to-r from-[#E94F9A] to-[#4B1E5A] mx-auto" />
      </div>
    </section>
  );
};


// --- Sticky Scroll CTA Mini-Bar ---
const StickyCTABar = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (location.pathname.startsWith("/admin") || location.pathname === "/donate") return null;

  return (
    <div
      data-testid="sticky-cta-bar"
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12 pointer-events-none"
      }`}
    >
      <div className="flex items-center gap-2.5 sm:gap-3 bg-[#4B1E5A]/95 backdrop-blur-xl border border-white/10 shadow-2xl rounded-full pl-5 sm:pl-6 pr-2.5 py-2.5">
        <span className="hidden sm:inline font-serif text-sm text-white pr-1">{"Ready to make a difference?"}</span>
        <Link
          to="/partners"
          data-testid="sticky-partner-btn"
          className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 sm:px-5 py-2.5 text-xs font-semibold transition-all whitespace-nowrap"
        >
          {"Partner With AGG"}
        </Link>
        <DonateButton variant="soft" showTagline={false} testId="sticky-donate-btn" />
      </div>
    </div>
  );
};

// --- Page Wrapper with Navbar & Footer ---
const AppLayout = ({ adminUser, handleLogout, children }) => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
    window.scrollTo(0, 0);
    const id = window.requestAnimationFrame(() => {
      const sections = Array.from(document.querySelectorAll("main section"));
      const targets = sections.slice(1); // keep the hero visible immediately
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("is-visible");
              obs.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      targets.forEach((el) => {
        el.classList.add("reveal-on-scroll");
        obs.observe(el);
      });
      window.__aggReveal = obs;
    });
    return () => {
      window.cancelAnimationFrame(id);
      if (window.__aggReveal) {
        window.__aggReveal.disconnect();
        window.__aggReveal = null;
      }
    };
  }, [location.pathname]);
  const showImagine = location.pathname === "/" || location.pathname === "/programs";
  const hideEmotional = location.pathname.startsWith("/admin");
  return (
    <div className="flex flex-col min-h-screen">
      <SEO pathname={location.pathname} />
      <Navbar adminUser={adminUser} handleLogout={handleLogout} />
      <main className="flex-grow">{children}</main>
      {showImagine && <ImagineHer />}
      {!hideEmotional && <OurPromise />}
      <Footer />
      <StickyCTABar />
    </div>
  );
};

// --- Main App Controller ---
function App() {
  const [adminUser, setAdminUser] = useState(null);

  // Auto-verify if user session exists on launch
  const checkSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      if (response.data && response.data.email) {
        setAdminUser(response.data);
      }
    } catch (err) {
      // Session doesn't exist, silently fail
      setAdminUser(null);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  const handleLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data && response.data.email) {
        setAdminUser(response.data);
      }
    } catch (err) {
      throw err; // bubble up error to form
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setAdminUser(null);
      toast.success("Logged out successfully.");
    } catch (err) {
      toast.error("Logout failed: " + formatApiError(err));
    }
  };

  return (
    <div className="App font-sans" data-testid="app-root">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><HomePage /></AppLayout>} />
          <Route path="/about" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><AboutPage /></AppLayout>} />
          <Route path="/founder" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><FounderPage /></AppLayout>} />
          <Route path="/programs" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><ProgramsPage /></AppLayout>} />
          <Route path="/who-we-serve" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><WhoWeServePage /></AppLayout>} />
          <Route path="/partners" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><PartnersPage /></AppLayout>} />
          <Route path="/mentor" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><BecomeAMentorPage /></AppLayout>} />
          <Route path="/volunteer" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><VolunteerPage /></AppLayout>} />
          <Route path="/donate" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><GivePage /></AppLayout>} />
          <Route path="/ways-to-give" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><WaysToGivePage /></AppLayout>} />
          <Route path="/shop" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><ShopPage /></AppLayout>} />
          <Route path="/schools" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><SchoolsPage /></AppLayout>} />
          <Route path="/events" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><EventsPage /></AppLayout>} />
          <Route path="/impact" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><ImpactPage /></AppLayout>} />
          <Route path="/contact" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><ContactPage /></AppLayout>} />
          <Route path="/admin" element={<AppLayout adminUser={adminUser} handleLogout={handleLogout}><AdminDashboard adminUser={adminUser} handleLogin={handleLogin} /></AppLayout>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
